import { useEffect } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { PageShell } from "@/components/site-chrome";
import { news, categoryOf, hoursAgo, readingTime } from "@/lib/news";
import { z } from "zod";

export const Route = createFileRoute("/")({
  validateSearch: z.object({
    cat: z.string().optional(),
  }),
  component: Home,
});

function seededShuffle<T>(array: T[], seed: string): T[] {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    hash = (hash * 9301 + 49297) % 233280;
    const j = Math.floor((hash / 233280) * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

function Home() {
  useEffect(() => {
    document.title = "Football News — Weborbis Sport";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute("content", "Latest football news, live match reports, tactical analysis and World Cup 2026 coverage from Weborbis Sport.");
    }
  }, []);

  const search = Route.useSearch();
  const seed = search?.cat ?? "home";
  
  const shuffledNews = seededShuffle(news, seed).filter(Boolean);
  const hero = shuffledNews[0] ?? news[0];
  const second = shuffledNews[1];
  const third = shuffledNews[2];
  const secondary = [second, third].filter(Boolean);
  const rest = shuffledNews.slice(3).filter(Boolean);

  return (
    <PageShell>
      <div className="container-page py-6">
        <section className="grid gap-6 lg:grid-cols-[2fr_1fr]">
          <Link
            to="/article/$id"
            params={{ id: hero.id }}
            className="group block overflow-hidden rounded-md border border-border bg-card"
          >
            <div className="aspect-[16/9] overflow-hidden bg-muted">
              <img
                src={hero.image}
                alt={hero.title}
                loading="eager"
                className="h-full w-full object-cover transition group-hover:scale-[1.02]"
              />
            </div>
            <div className="p-5">
              <h1 className="text-2xl md:text-3xl font-bold leading-tight group-hover:underline">
                {hero.title}
              </h1>
              <p className="mt-3 text-muted-foreground">{hero.excerpt}</p>
              <div className="mt-4 flex items-center gap-3 text-xs text-muted-foreground">
                <span>{hoursAgo(hero.id)}h ago</span>
                <span className="inline-block bg-brand text-brand-foreground px-2 py-0.5 rounded-sm font-semibold">
                  {categoryOf(hero.title)}
                </span>
                <span>{readingTime(hero.wordCount)} min read</span>
              </div>
            </div>
          </Link>

          <div className="flex flex-col gap-4">
            {secondary.map((n) => (
              <Link
                key={n.id}
                to="/article/$id"
                params={{ id: n.id }}
                className="group flex gap-3 rounded-md border border-border bg-card overflow-hidden"
              >
                <div className="w-32 shrink-0 bg-muted overflow-hidden">
                  <img src={n.image} alt={n.title} className="h-full w-full object-cover" />
                </div>
                <div className="p-3">
                  <h3 className="font-semibold leading-snug group-hover:underline line-clamp-3">
                    {n.title}
                  </h3>
                  <div className="mt-2 flex items-center gap-2 text-[11px] text-muted-foreground">
                    <span>{hoursAgo(n.id)}h ago</span>
                    <span className="bg-secondary px-1.5 py-0.5 rounded">
                      {categoryOf(n.title)}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <div className="mt-10 border-t-4 border-ink pt-2">
          <h2 className="text-lg font-bold">Latest Football News</h2>
        </div>

        <section className="mt-4 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {rest.map((n) => (
            <Link
              key={n.id}
              to="/article/$id"
              params={{ id: n.id }}
              className="group block rounded-md border border-border bg-card overflow-hidden"
            >
              <div className="aspect-[16/9] overflow-hidden bg-muted">
                <img
                  src={n.image}
                  alt={n.title}
                  loading="lazy"
                  className="h-full w-full object-cover transition group-hover:scale-[1.02]"
                />
              </div>
              <div className="p-4">
                <h3 className="font-semibold leading-snug group-hover:underline line-clamp-3">
                  {n.title}
                </h3>
                <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{n.excerpt}</p>
                <div className="mt-3 flex items-center gap-2 text-[11px] text-muted-foreground">
                  <span>{hoursAgo(n.id)}h ago</span>
                  <span className="bg-secondary px-1.5 py-0.5 rounded">
                    {categoryOf(n.title)}
                  </span>
                  <span>· {readingTime(n.wordCount)} min</span>
                </div>
              </div>
            </Link>
          ))}
        </section>
      </div>
    </PageShell>
  );
}